import ErrorAlert from "@/utility/ErrorAlert";
import http from "@/axios/http-common";

const BASE = "/kmkp/audit-kuantitatif-ranap";

export type NilaiDokumen = "L" | "TL" | "NA";

export interface KetepatanBerkas {
  status: "tepat" | "terlambat" | "belum_ttd" | "belum_diisi" | "tidak_terbaca" | null;
  kode: "T" | "TT" | null;
  pulang_at: string | null;
  ttd_at: string | null;
}

export interface BerkasRanap {
  id: number;
  register_id: string;
  no_rkm_medis: string;
  nama_pasien: string | null;
  tanggal_lahir: string | null;
  tanggal_admisi: string | null;
  tanggal_pulang: string;
  unit_id: string;
  unit_pelayanan: string | null;
  dpjp_id: string | null;
  dpjp_nama: string | null;
  asesmen_medis: NilaiDokumen;
  cppt: NilaiDokumen;
  informed_consent: NilaiDokumen;
  laporan_operasi: NilaiDokumen;
  ringkasan_pulang: NilaiDokumen;
  catatan: string | null;
  pelaksana_nama: string | null;
  ketepatan: KetepatanBerkas | null;
}

export interface BerkasRanapInput {
  no_rkm_medis: string;
  register_id: string;
  asesmen_medis: NilaiDokumen | null;
  cppt: NilaiDokumen | null;
  informed_consent: NilaiDokumen | null;
  laporan_operasi: NilaiDokumen | null;
  ringkasan_pulang: NilaiDokumen | null;
  catatan: string;
}

export interface RegisterRanapAudit {
  register: string;
  no_rkm_medis: string;
  nama_pasien: string | null;
  tanggal_lahir: string | null;
  masuk: string;
  jam_masuk: string | null;
  pulang: string | null;
  jam_pulang: string | null;
  unit_id: string;
  unit_nama: string;
  dpjp_id: string | null;
  dpjp_nama: string | null;
  ketepatan: KetepatanBerkas | null;
  berkas_id: number | null;
}

export interface LaporanBangsalNilai {
  l: number;
  tl: number;
  persen: number | null;
}

export interface LaporanBangsalKetepatan {
  t: number | null;
  tt: number | null;
  persen: number | null;
}

export interface LaporanBangsalBaris {
  no: number | null;
  nama: string;
  unit_id: string | null;
  jumlah: number;
  kolom: Record<string, LaporanBangsalNilai>;
  ketepatan: LaporanBangsalKetepatan;
}

export interface LaporanBangsal {
  tahun: number;
  bulan: number;
  bulan_label: string;
  total_berkas: number;
  kolom: Record<string, string>;
  label_ketepatan: string;
  rows: LaporanBangsalBaris[];
  jumlah: LaporanBangsalBaris;
  ketepatan_terbaca: boolean;
  catatan: string[];
}

export interface Periode {
  tahun: number;
  bulan: number;
}

function handle<T>(p: Promise<T>): Promise<T> {
  return p.catch(err => {
    ErrorAlert.showErrorAlert(err);
    return Promise.reject(err);
  });
}

function bacaBlob(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

async function galatBlob(err: { response?: { data?: unknown } }): Promise<never> {
  const data = err.response?.data;
  if (err.response && data instanceof Blob) {
    try {
      err.response.data = JSON.parse(await bacaBlob(data));
    } catch {
      err.response.data = { message: "Berkas gagal dibuat" };
    }
  }
  ErrorAlert.showErrorAlert(err);
  return Promise.reject(err);
}

const getList = async (p: Periode): Promise<BerkasRanap[]> => {
  const res: any = await handle(http.getDataParam(BASE, { params: p }));
  return (res.data && res.data.data) || [];
};

const create = (data: BerkasRanapInput) => handle(http.postData(BASE, data));
const update = (id: number, data: BerkasRanapInput) => handle(http.putData(`${BASE}/${id}`, data));
const destroy = (id: number) => handle(http.deleteData(`${BASE}/${id}`));

const getRegisterRanap = async (noRm: string): Promise<RegisterRanapAudit[]> => {
  const res: any = await handle(http.getDataParam(`${BASE}-register`, { params: { no_rm: noRm } }));
  return (res.data && res.data.data) || [];
};

const getLaporanBangsal = async (p: Periode): Promise<LaporanBangsal> => {
  const res: any = await handle(http.getDataParam(`${BASE}-laporan`, { params: p }));
  return res.data.data;
};

const openLaporanBangsalPdf = async (p: Periode): Promise<void> => {
  const res: any = await http
    .getDataParam(`${BASE}-laporan/pdf`, { params: p, responseType: "blob" })
    .catch(galatBlob);
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const downloadLaporanBangsalExcel = async (p: Periode): Promise<void> => {
  const res: any = await http
    .getDataParam(`${BASE}-laporan/excel`, { params: p, responseType: "blob" })
    .catch(galatBlob);
  const url = URL.createObjectURL(
    new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `laporan_analisis_kuantitatif_bangsal_${p.tahun}-${String(p.bulan).padStart(2, "0")}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export default {
  getList,
  create,
  update,
  destroy,
  getRegisterRanap,
  getLaporanBangsal,
  openLaporanBangsalPdf,
  downloadLaporanBangsalExcel,
};
